import { UploadTaskConfig, UploadTaskListeners } from '../types';

import UploadTask from './UploadTask';
import UploadWorker from './UploadWorker';

export interface UploadManagerOptions {
  workerCount: number;
}

type UploadTaskRecord = Record<string, UploadTask>;

export default class UploadManager {
  // Map is used because it iterates in insertion order, giving us a queue with direct lookups as well
  uploadTasks: Map<string, UploadTask>;

  workerPool: UploadWorker[];

  persistenceGroups: Record<string, UploadTaskRecord>;

  constructor({ workerCount }: UploadManagerOptions) {
    this.uploadTasks = new Map();
    this.workerPool = Array.from(Array(workerCount)).map(() => new UploadWorker());
    this.persistenceGroups = {};
    setInterval(() => this.processQueue(), 1000);
  }

  add(tasks: UploadTaskConfig[], listeners?: UploadTaskListeners): UploadTaskRecord {
    const addedTasks: UploadTaskRecord = {};
    // at this point only images need the options
    tasks.forEach(({ file, opts }) => {
      const task = new UploadTask(file, opts, listeners);
      this.uploadTasks.set(task.id, task);
      addedTasks[task.id] = task;
      // optional persistence key to group tasks
      if (opts?.persistenceKey) {
        if (this.persistenceGroups[opts.persistenceKey]) {
          this.persistenceGroups[opts.persistenceKey][task.id] = task;
        } else {
          this.persistenceGroups[opts.persistenceKey] = { [task.id]: task };
        }
      }
    });
    return addedTasks;
  }

  get nextQueuedTask(): UploadTask | undefined {
    const queuedTasks = [...this.uploadTasks].filter(([, task]) => task.status === 'queued');
    return queuedTasks[0] ? queuedTasks[0][1] : undefined;
  }

  get availableWorker(): UploadWorker | undefined {
    return this.workerPool.find((worker) => worker.currentTask === undefined);
  }

  processQueue(): void {
    // attempt to start tasks on all workers
    while (this.nextQueuedTask && this.availableWorker) {
      // eslint-disable-next-line no-console
      console.log(`starting task ${this.nextQueuedTask.id} on a worker`);
      this.availableWorker.startTask(this.nextQueuedTask);
    }
  }

  getPersistedTasksForKey(key: string): UploadTaskRecord | undefined {
    return this.persistenceGroups[key];
  }

  removePersistedTaskByKey(key: string, taskId: string): void {
    if (this.persistenceGroups[key]) {
      delete this.persistenceGroups[key][taskId];
    }
  }
}
